// src/pages/misc/landing/useLandingChrome.ts
// The landing's "chrome" (everything around the content) as one hook, so other pages can look and move exactly like /landing:
//   - top bar turns solid after 40px of scroll
//   - left progress line (.vl-thread-fill) grows with page scroll
//   - thin right scrollbar (.vl-scrollbar) replaces the browser's bar, draggable, min 72px thumb
//   - constellation lines (.vl-bg-detail) drift: x = -0.012 x scrollY, y = -0.03 x scrollY
//   - every .vl-reveal under `rootSelector` fades in once 15% is visible
// [HANDOFF] Used by public/SociosPage.tsx. LandingPage.tsx still has its own copy of this logic inside its big scroll loop
//   (same numbers); it can switch to this hook later. Keep the numbers here and there identical.
import React, { useEffect, useRef, useState } from 'react';

const THUMB_MIN = 72;
const clamp = (v: number, a: number, b: number) => Math.max(a, Math.min(b, v));

export function useLandingChrome(rootSelector: string) {
  const threadFillRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLDivElement>(null);
  const bgDetailRef = useRef<SVGSVGElement>(null);
  const dragRef = useRef<{ y: number; scroll: number } | null>(null);
  const [topSolid, setTopSolid] = useState(false);

  useEffect(() => {
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    document.documentElement.classList.add('vl-no-native-bar');

    let ticking = false;
    const paint = () => {
      ticking = false;
      const y = window.scrollY;
      const h = window.innerHeight;
      const doc = document.documentElement.scrollHeight - h;
      const pct = clamp(y / Math.max(doc, 1), 0, 1);
      if (threadFillRef.current) threadFillRef.current.style.height = `${pct * 100}%`;
      const thumb = thumbRef.current;
      if (thumb) {
        const thumbH = Math.max(THUMB_MIN, (h * h) / (doc + h));
        thumb.style.height = `${thumbH}px`;
        thumb.style.transform = `translate3d(0,${(pct * (h - thumbH)).toFixed(1)}px,0)`;
      }
      setTopSolid(y > 40);
      if (!reduce && bgDetailRef.current) {
        bgDetailRef.current.style.transform = `translate3d(${-y * 0.012}px,${-y * 0.03}px,0)`;
      }
    };
    const onScroll = () => {
      if (!ticking) {
        ticking = true;
        requestAnimationFrame(paint);
      }
    };
    paint();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('in');
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.15 },
    );
    document.querySelectorAll(`${rootSelector} .vl-reveal`).forEach((el) => io.observe(el));

    return () => {
      document.documentElement.classList.remove('vl-no-native-bar');
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      io.disconnect();
    };
  }, [rootSelector]);

  const range = () => {
    const h = window.innerHeight;
    const doc = document.documentElement.scrollHeight - h;
    const thumbH = thumbRef.current?.offsetHeight ?? THUMB_MIN;
    return { doc, travel: Math.max(h - thumbH, 1), thumbH };
  };

  // Spread these on the .vl-scrollbar element: drag the thumb, or press the rail to jump there.
  const barHandlers = {
    onPointerDown: (e: React.PointerEvent<HTMLDivElement>) => {
      e.preventDefault();
      const { doc, travel, thumbH } = range();
      if (e.target !== thumbRef.current) {
        window.scrollTo({ top: clamp((e.clientY - thumbH / 2) / travel, 0, 1) * doc, behavior: 'auto' });
      }
      try {
        e.currentTarget.setPointerCapture(e.pointerId);
      } catch {
        /* Pointer can already be gone; the jump still happened. */
      }
      dragRef.current = { y: e.clientY, scroll: window.scrollY };
    },
    onPointerMove: (e: React.PointerEvent<HTMLDivElement>) => {
      const drag = dragRef.current;
      if (!drag) return;
      const { doc, travel } = range();
      window.scrollTo({ top: drag.scroll + ((e.clientY - drag.y) / travel) * doc, behavior: 'auto' });
    },
    onPointerUp: () => {
      dragRef.current = null;
    },
    onPointerCancel: () => {
      dragRef.current = null;
    },
  };

  return { threadFillRef, thumbRef, bgDetailRef, topSolid, barHandlers };
}
